import type { AnimationAction, AnimationMixer, Color, DirectionalLight, HemisphereLight, Material, Mesh, MeshStandardMaterial, Object3D, PerspectiveCamera, Scene, Texture, Vector3, WebGLRenderer } from "three";
import type { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { SkinAnimation, SkinEffectMode, SkinEffectPattern, SkinEnvironmentMode } from "./types.js";

export interface SkinPreviewEffectSettings {
  effectMode: SkinEffectMode;
  effectColor1: string;
  effectColor2: string;
  effectColor3: string;
  effectSpeedSeconds: number;
  effectPattern: SkinEffectPattern;
  environmentMode: SkinEnvironmentMode;
}

export interface SkinPreviewManifest {
  schemaVersion: number;
  gameBuild: string;
  models: SkinPreviewModel[];
}

export interface SkinPreviewModel {
  species: string;
  file: string;
  sha256: string;
  verifiedAgainstBuild: string;
  licenceFile: string;
  attribution: string;
  rendering: {
    mode: "evrima-mask-v1";
    bodyMaterial: string;
    eyeMaterial: string;
    patternFile: string;
    patternFiles?: string[];
    juvenilePatternFile?: string;
    hatchlingPatternFile?: string;
    growthMorphEyes?: boolean;
    visualGrowthCurve?: GrowthCurveKey[];
    themeCounts?: number[];
    partMaskFile: string;
    paletteChannels: Record<string, "red" | "green" | "blue" | "cyan" | "magenta" | "yellow">;
    partChannels: Record<string, "red" | "green" | "blue">;
    fieldBindings?: Record<string, string>;
  };
  review: { status: string; reviewedAt: string; evidenceFiles: string[] };
}

interface ActivePreview {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  controls: OrbitControls;
  root: Object3D;
  growthMorphMeshes: GrowthMorphMesh[];
  visualGrowthCurve?: GrowthCurveKey[];
  coloursByChannel: Map<string, Color[]>;
  bodyMaterials: MeshStandardMaterial[];
  patternTextures: GrowthPatternTextures;
  selectedPattern: number;
  growthPercent: number;
  goreIntensity: number;
  effectSettings: SkinPreviewEffectSettings;
  animation?: SkinAnimation;
  textures: Texture[];
  mixer?: AnimationMixer;
  lastFrameAtMs: number;
  motionActions: Map<SkinPreviewActionKey, AnimationAction>;
  activeMotion?: SkinPreviewMotion;
  activeLifeStage?: SkinPreviewLifeStage;
  reframe: () => void;
  hemisphereLight: HemisphereLight;
  keyLight: DirectionalLight;
  rimLight: DirectionalLight;
  lightingMode: SkinPreviewLighting;
  lightIntensity: number;
  resizeObserver: ResizeObserver;
  removeControlListeners: () => void;
  removeMixerListeners: () => void;
  frame: number;
}

interface GrowthMorphMesh {
  morphTargetDictionary: Record<string, number>;
  morphTargetInfluences: number[];
}

interface GrowthPatternTextures {
  adult: Texture[];
  juvenile?: Texture;
  hatchling?: Texture;
}

export interface GrowthCurveKey {
  time: number;
  value: number;
  interpolation?: "linear" | "cubic";
  arriveTangent?: number;
  leaveTangent?: number;
}

interface ActiveMarketplacePreview {
  host: HTMLElement;
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  root: Object3D;
  growthMorphMeshes: GrowthMorphMesh[];
  visualGrowthCurve?: GrowthCurveKey[];
  bodyMaterials: MeshStandardMaterial[];
  patternTextures: GrowthPatternTextures;
  selectedPattern: number;
  growthPercent: number;
  textures: Texture[];
  resizeObserver: ResizeObserver;
}

interface ActiveLiveDinoPreview {
  species: string;
  host: HTMLElement;
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  root: Object3D;
  coloursByChannel: Map<string, Color[]>;
  textures: Texture[];
  resizeObserver: ResizeObserver;
}

export type SkinPaletteLayer = "red" | "green" | "blue" | "cyan" | "magenta" | "yellow";
export type SkinPartLayer = "red" | "green" | "blue";
export type SkinPreviewMotion = "idle" | "rest" | "walk" | "run" | "attack" | "threaten" | "attract" | "broadcast" | "danger" | "friendly" | "sniff" | "drink" | "eat" | "swim" | "wallow";
export type SkinPreviewLifeStage = "juvenile" | "subadult" | "adult";
export type SkinPreviewActionKey = `${SkinPreviewLifeStage}:${SkinPreviewMotion}`;
export type SkinPreviewLighting = "containment" | "daylight" | "night";

const previewMotionAliases: Readonly<Record<SkinPreviewMotion, readonly string[]>> = {
  idle: ["idle", "breathe", "stand"],
  rest: ["rest", "sleep", "sit"],
  walk: ["walk"],
  run: ["run", "sprint"],
  attack: ["attack", "bite", "primary attack", "secondary attack"],
  threaten: ["threaten", "threat"],
  attract: ["attract"],
  broadcast: ["broadcast"],
  danger: ["danger"],
  friendly: ["friendly call", "friendly", "f call"],
  sniff: ["sniff", "scent"],
  drink: ["drink"],
  eat: ["eat", "feed"],
  swim: ["swim"],
  wallow: ["wallow"]
};
const loopingPreviewMotions = new Set<SkinPreviewMotion>([
  "idle", "rest", "walk", "run", "sniff", "drink", "eat", "swim"
]);

export function skinPreviewMotionLoops(motion: SkinPreviewMotion): boolean {
  return loopingPreviewMotions.has(motion);
}

export function skinPreviewLifeStage(growthPercent: number): SkinPreviewLifeStage {
  const growth = Number.isFinite(growthPercent) ? Math.max(0, Math.min(100, growthPercent)) : 100;
  if (growth <= 35) return "juvenile";
  if (growth <= 85) return "subadult";
  return "adult";
}

export function skinPreviewActionKey(stage: SkinPreviewLifeStage, motion: SkinPreviewMotion): SkinPreviewActionKey {
  return `${stage}:${motion}`;
}

// Diagnostic palettes applied in game confirmed that the extracted mask files
// label these regions differently from the skin payload fields. Keep the game
// payload untouched and translate only the web preview binding.
const previewMaskFieldCorrections: Readonly<Record<string, string>> = {
  MaleDisplayColor: "MarkingsColor",
  MarkingsColor: "BodyColor",
  BodyColor: "UnderbellyColor",
  FlankColor: "FlankColor",
  UnderbellyColor: "MaleDisplayColor",
  MouthColor: "TeethColor",
  TeethColor: "ClawsColor",
  ClawsColor: "MouthColor"
};

export function previewFieldForExtractedMask(field: string, fieldBindings?: Readonly<Record<string, string>>): string {
  return fieldBindings?.[field] ?? previewMaskFieldCorrections[field] ?? field;
}

export function skinPreviewMaterialSupportsGrowthMorph(materialName: string, eyeMaterial: string): boolean {
  return materialName !== eyeMaterial;
}

export function skinPaletteMaskWeights(red: number, green: number, blue: number): Record<SkinPaletteLayer, number> {
  return {
    red: Math.max(red - Math.max(green, blue), 0),
    green: Math.max(green - Math.max(red, blue), 0),
    blue: Math.max(blue - Math.max(red, green), 0),
    cyan: Math.min(green, blue) * (1 - red),
    magenta: Math.min(red, blue) * (1 - green),
    yellow: Math.min(red, green) * (1 - blue)
  };
}

export function normalizedSkinPartMaskWeights(red: number, green: number, blue: number): Record<SkinPartLayer, number> {
  const weights = {
    red: smoothMask(Math.max(red - Math.max(green, blue), 0)),
    green: smoothMask(Math.max(green - Math.max(red, blue), 0)),
    blue: smoothMask(Math.max(blue - Math.max(red, green), 0))
  };
  const total = weights.red + weights.green + weights.blue;
  if (total <= Number.EPSILON) return { red: 0, green: 0, blue: 0 };
  return { red: weights.red / total, green: weights.green / total, blue: weights.blue / total };
}

function smoothMask(value: number): number {
  const normalized = clampUnit((value - 0.2) / 0.6);
  return normalized * normalized * (3 - 2 * normalized);
}

let activePreview: ActivePreview | undefined;
let activeLiveDinoPreview: ActiveLiveDinoPreview | undefined;
let activeMarketplacePreviews = new Map<HTMLElement, ActiveMarketplacePreview>();
let loadingMarketplacePreviews = new Set<HTMLElement>();
let marketplacePreviewObserver: IntersectionObserver | undefined;
let marketplacePreviewLoadQueue: Promise<void> = Promise.resolve();
let marketplaceManifestPromise: Promise<SkinPreviewManifest> | undefined;
let initializationSequence = 0;
let liveDinoInitializationSequence = 0;
let marketplaceInitializationSequence = 0;

export function findApprovedSkinPreviewModel(manifest: SkinPreviewManifest, species: string): SkinPreviewModel | undefined {
  if (manifest.schemaVersion !== 1 || !manifest.gameBuild.trim()) return undefined;
  const normalizedSpecies = normalizePreviewSpecies(species);
  return manifest.models.find((model) => normalizePreviewSpecies(model.species) === normalizedSpecies
    && model.review.status === "approved"
    && model.verifiedAgainstBuild === manifest.gameBuild);
}

export function normalizePreviewSpecies(value: string): string {
  const leaf = value.trim().split(/[\\/]/).at(-1) ?? value;
  const normalized = leaf
    .replace(/^BP_/i, "")
    .replace(/(?:\.[^.]+)?_C$/i, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
  return normalized === "deinonychus" || normalized === "utahraptor" ? "omniraptor" : normalized;
}

export function mapSkinPreviewAnimationClips(clips: readonly { name: string }[]): Partial<Record<SkinPreviewActionKey, number>> {
  const mapped: Partial<Record<SkinPreviewActionKey, number>> = {};
  clips.forEach((clip, index) => {
    let normalized = clip.name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    if (!normalized) return;
    let stage: SkinPreviewLifeStage = "adult";
    for (const candidate of ["juvenile", "subadult", "adult"] as const) {
      if (normalized === candidate || normalized.startsWith(`${candidate} `)) {
        stage = candidate;
        normalized = normalized.slice(candidate.length).trim();
        break;
      }
    }
    for (const [motion, aliases] of Object.entries(previewMotionAliases) as Array<[SkinPreviewMotion, readonly string[]]>) {
      const key = skinPreviewActionKey(stage, motion);
      if (mapped[key] !== undefined) continue;
      const matched = aliases.some((alias) => {
        const normalizedAlias = alias.replace(/[^a-z0-9]+/g, " ").trim();
        return normalized === normalizedAlias
          || normalized.startsWith(`${normalizedAlias} `)
          || normalized.endsWith(` ${normalizedAlias}`)
          || normalized.includes(` ${normalizedAlias} `);
      });
      if (matched) {
        mapped[key] = index;
        break;
      }
    }
  });
  return mapped;
}

export async function initializeSkinModelPreview(): Promise<void> {
  disposeSkinModelPreview();
  const host = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  const species = host?.dataset.skinPreviewSpecies?.trim();
  if (!host || !species) return;

  const canvas = host.querySelector<HTMLCanvasElement>("canvas");
  const status = host.querySelector<HTMLElement>("[data-skin-preview-status]");
  const unavailable = host.querySelector<HTMLElement>("[data-skin-preview-unavailable]");
  const resetButton = host.querySelector<HTMLButtonElement>("[data-skin-preview-reset]");
  const rotateButton = host.querySelector<HTMLButtonElement>("[data-skin-preview-rotate]");
  if (!canvas || !status || !unavailable || !resetButton || !rotateButton) return;

  setPreviewState(host, canvas, unavailable, resetButton, rotateButton, "checking");
  status.textContent = "Checking the verified model catalogue...";

  const sequence = ++initializationSequence;
  let manifest: SkinPreviewManifest;
  try {
    const response = await fetch("/portal/assets/skins/manifest.json", { cache: "no-store", credentials: "same-origin" });
    if (!response.ok) throw new Error(`Skin model catalogue returned ${response.status}`);
    manifest = await response.json() as SkinPreviewManifest;
  } catch {
    if (sequence === initializationSequence && host.isConnected) {
      setPreviewState(host, canvas, unavailable, resetButton, rotateButton, "error");
      status.textContent = "The 3D model catalogue could not be checked. Live colour editing is still available.";
    }
    return;
  }

  if (sequence !== initializationSequence || !host.isConnected) return;
  const model = findApprovedSkinPreviewModel(manifest, species);
  if (!model) {
    setPreviewState(host, canvas, unavailable, resetButton, rotateButton, "unavailable");
    status.textContent = `No licensed, game-verified ${species} model is approved for this EVRIMA build yet.`;
    return;
  }

  setPreviewState(host, canvas, unavailable, resetButton, rotateButton, "loading");
  status.textContent = `Loading verified ${model.species} preview...`;

  try {
    const compatibilityMode = host.dataset.skinPreviewCompatibility === "true";
    const [THREE, loaderModule, controlsModule] = await Promise.all([
      import("three"),
      import("three/addons/loaders/GLTFLoader.js"),
      import("three/addons/controls/OrbitControls.js")
    ]);
    if (sequence !== initializationSequence || !host.isConnected) return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true, powerPreference: compatibilityMode ? "default" : "high-performance" });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(compatibilityMode ? 1 : Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 2_000);
    const controls = new controlsModule.OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.enablePan = false;
    controls.minDistance = 0.5;
    controls.maxDistance = 40;

    const hemisphereLight = new THREE.HemisphereLight(0xf1e7c7, 0x16251c, 1.8);
    scene.add(hemisphereLight);
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const keyLight = new THREE.DirectionalLight(0xfff2d0, 2.4);
    keyLight.position.set(4, 7, 6);
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(0x7cbfa2, 1.2);
    rimLight.position.set(-5, 3, -4);
    scene.add(rimLight);

    const loader = new loaderModule.GLTFLoader();
    const gltf = await loader.loadAsync(skinAssetUrl(model.file));
    if (sequence !== initializationSequence || !host.isConnected) {
      disposeObject(gltf.scene);
      renderer.dispose();
      return;
    }

    const prepared = await prepareEvrimaMaterials(THREE, gltf.scene, model, true);
    scene.add(gltf.scene);
    camera.aspect = Math.max(1, host.clientWidth) / Math.max(1, canvas.clientHeight);
    const originalRootPosition = gltf.scene.position.clone();
    let homeView = frameObject(THREE, gltf.scene, camera, controls);
    applyCurrentEditorColours(prepared.coloursByChannel);
    const selectedPattern = Number(document.querySelector<HTMLSelectElement>("[data-skin-pattern]")?.value);
    applyPreviewPattern(prepared.bodyMaterials, prepared.patternTextures.adult, selectedPattern);
    const mixer = gltf.animations.length ? new THREE.AnimationMixer(gltf.scene) : undefined;
    const motionActions = new Map<SkinPreviewActionKey, AnimationAction>();
    if (mixer) {
      const mappedClips = mapSkinPreviewAnimationClips(gltf.animations);
      for (const [key, clipIndex] of Object.entries(mappedClips) as Array<[SkinPreviewActionKey, number]>) {
        const clip = gltf.animations[clipIndex];
        if (!clip) continue;
        const action = mixer.clipAction(clip);
        const motion = key.slice(key.indexOf(":") + 1) as SkinPreviewMotion;
        if (!skinPreviewMotionLoops(motion)) action.setLoop(THREE.LoopOnce, 1);
        motionActions.set(key, action);
      }
    }
    syncSkinPreviewMotionButtons(host, motionActions, "adult");

    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, canvas.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    const resetView = () => {
      camera.position.copy(homeView.cameraPosition);
      controls.target.copy(homeView.target);
      controls.update();
    };
    const reframe = () => {
      gltf.scene.position.copy(originalRootPosition);
      gltf.scene.updateMatrixWorld(true);
      homeView = frameObject(THREE, gltf.scene, camera, controls);
    };
    const toggleRotation = () => {
      controls.autoRotate = !controls.autoRotate;
      controls.autoRotateSpeed = 1.15;
      rotateButton.setAttribute("aria-pressed", String(controls.autoRotate));
      rotateButton.classList.toggle("active", controls.autoRotate);
      const label = rotateButton.querySelector<HTMLElement>("[data-skin-rotate-label]");
      if (label) label.textContent = controls.autoRotate ? "Pause rotation" : "Auto rotate";
    };
    resetButton.addEventListener("click", resetView);
    rotateButton.addEventListener("click", toggleRotation);
    const removeControlListeners = () => {
      resetButton.removeEventListener("click", resetView);
      rotateButton.removeEventListener("click", toggleRotation);
    };
    const returnToIdle = () => {
      const current = activePreview;
      if (!current || current.mixer !== mixer || !current.activeMotion || skinPreviewMotionLoops(current.activeMotion)) return;
      const stage = skinPreviewLifeStage(current.growthPercent);
      if (current.motionActions.has(skinPreviewActionKey(stage, "idle"))) playSkinModelPreviewMotion("idle");
    };
    mixer?.addEventListener("finished", returnToIdle);
    const removeMixerListeners = () => mixer?.removeEventListener("finished", returnToIdle);

    const state: ActivePreview = {
      renderer,
      scene,
      camera,
      controls,
      root: gltf.scene,
      growthMorphMeshes: collectGrowthMorphMeshes(gltf.scene, model.rendering.eyeMaterial, model.rendering.growthMorphEyes),
      visualGrowthCurve: model.rendering.visualGrowthCurve,
      coloursByChannel: prepared.coloursByChannel,
      bodyMaterials: prepared.bodyMaterials,
      patternTextures: prepared.patternTextures,
      selectedPattern: Number.isSafeInteger(selectedPattern) && selectedPattern >= 0 ? selectedPattern : 0,
      growthPercent: 100,
      goreIntensity: 0,
      effectSettings: defaultSkinPreviewEffectSettings(),
      textures: prepared.textures,
      ...(mixer ? { mixer } : {}),
      lastFrameAtMs: performance.now(),
      motionActions,
      reframe,
      hemisphereLight,
      keyLight,
      rimLight,
      lightingMode: "containment",
      lightIntensity: 1,
      resizeObserver,
      removeControlListeners,
      removeMixerListeners,
      frame: 0
    };
    const renderFrame = () => {
      if (activePreview !== state || !host.isConnected) return;
      if (state.animation?.enabled) applyRegionAnimationPreview(state, performance.now());
      else if (state.effectSettings.effectMode !== "none") applySkinEffectPreview(state, performance.now(), host);
      const frameAtMs = performance.now();
      const frameDeltaSeconds = Math.min(Math.max(frameAtMs - state.lastFrameAtMs, 0) / 1_000, 0.05);
      state.lastFrameAtMs = frameAtMs;
      state.mixer?.update(frameDeltaSeconds);
      controls.update();
      renderer.render(scene, camera);
      state.frame = window.requestAnimationFrame(renderFrame);
    };
    activePreview = state;
    const requestedLighting = host.dataset.skinPreviewLighting;
    updateSkinModelPreviewLighting(
      requestedLighting === "daylight" || requestedLighting === "night" ? requestedLighting : "containment",
      Number(host.dataset.skinPreviewIntensity ?? "100")
    );
    updateSkinModelPreviewGrowth(Number(host.dataset.skinPreviewGrowth ?? "100"));
    updateSkinModelPreviewEffects(
      Number(document.querySelector<HTMLInputElement>("[data-skin-gore-intensity]")?.value ?? "0"),
      readPreviewEffectSettingsFromDom()
    );
    updateSkinModelPreviewAnimation(parseSkinPreviewAnimationState(host.dataset.skinPreviewAnimation));
    const requestedMotion = host.dataset.skinPreviewMotion as SkinPreviewMotion | undefined;
    if (!(requestedMotion && playSkinModelPreviewMotion(requestedMotion))) playSkinModelPreviewMotion("idle");
    setPreviewState(host, canvas, unavailable, resetButton, rotateButton, "ready");
    renderFrame();
    status.textContent = `${model.species} model verified for EVRIMA ${model.verifiedAgainstBuild}. ${motionActions.size ? `${motionActions.size} game-stage motion clips ready.` : "Static inspection pack; action controls unlock when verified clips are added."} ${model.attribution}`;
  } catch (error) {
    console.warn("Skin preview initialization failed.", error);
    if (host.dataset.skinPreviewCompatibility !== "true" && sequence === initializationSequence && host.isConnected) {
      host.dataset.skinPreviewCompatibility = "true";
      status.textContent = "Retrying the verified model with compatibility rendering...";
      window.setTimeout(() => void initializeSkinModelPreview(), 350);
      return;
    }
    setPreviewState(host, canvas, unavailable, resetButton, rotateButton, "error");
    status.textContent = "The verified model could not be loaded on this device. Palette editing and live application are still available.";
  }
}

export async function initializeLiveDinoModelPreview(
  colors: Array<{ field: string; r: number; g: number; b: number; a: number }> | null | undefined
): Promise<void> {
  const host = document.querySelector<HTMLElement>("[data-live-dino-model-preview]");
  const species = host?.dataset.liveDinoSpecies?.trim();
  const canvas = host?.querySelector<HTMLCanvasElement>("canvas");
  if (!host || !canvas || !species) return;

  if (activeLiveDinoPreview
    && activeLiveDinoPreview.host === host
    && activeLiveDinoPreview.species.toLowerCase() === species.toLowerCase()) {
    applyLiveDinoColours(activeLiveDinoPreview, colors);
    activeLiveDinoPreview.renderer.render(activeLiveDinoPreview.scene, activeLiveDinoPreview.camera);
    host.hidden = false;
    canvas.hidden = false;
    return;
  }
  disposeLiveDinoModelPreview();

  const sequence = ++liveDinoInitializationSequence;
  try {
    const response = await fetch("/portal/assets/skins/manifest.json", { cache: "no-store", credentials: "same-origin" });
    if (!response.ok) return;
    const manifest = await response.json() as SkinPreviewManifest;
    const model = findApprovedSkinPreviewModel(manifest, species);
    if (!model || sequence !== liveDinoInitializationSequence || !host.isConnected) return;

    host.dataset.liveDinoModelState = "loading";
    const [THREE, loaderModule] = await Promise.all([
      import("three"),
      import("three/addons/loaders/GLTFLoader.js")
    ]);
    if (sequence !== liveDinoInitializationSequence || !host.isConnected) return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "high-performance" });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(30, 1, 0.01, 2_000);
    scene.add(new THREE.HemisphereLight(0xfff1cf, 0x14241a, 2.6));
    const keyLight = new THREE.DirectionalLight(0xffedc1, 3.5);
    keyLight.position.set(4, 7, 6);
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(0x83c9a9, 1.7);
    rimLight.position.set(-5, 3, -4);
    scene.add(rimLight);

    const gltf = await new loaderModule.GLTFLoader().loadAsync(skinAssetUrl(model.file));
    if (sequence !== liveDinoInitializationSequence || !host.isConnected) {
      disposeObject(gltf.scene);
      renderer.dispose();
      return;
    }
    const prepared = await prepareEvrimaMaterials(THREE, gltf.scene, model);
    for (const color of colors ?? []) {
      for (const target of prepared.coloursByChannel.get(color.field) ?? []) {
        target.setRGB(clampUnit(color.r), clampUnit(color.g), clampUnit(color.b));
      }
    }
    scene.add(gltf.scene);
    const bounds = new THREE.Box3().setFromObject(gltf.scene);
    const size = bounds.getSize(new THREE.Vector3());
    const centre = bounds.getCenter(new THREE.Vector3());
    gltf.scene.position.sub(centre);
    const diameter = Math.max(size.x, size.y, size.z, 0.1);
    const distance = diameter / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2))) * 0.82;
    camera.near = Math.max(distance / 1_000, 0.01);
    camera.far = distance * 100;
    camera.position.set(distance * 0.72, diameter * 0.12, distance * 0.92);
    camera.lookAt(0, -diameter * 0.04, 0);
    camera.updateProjectionMatrix();

    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    const state: ActiveLiveDinoPreview = {
      species,
      host,
      renderer,
      scene,
      camera,
      root: gltf.scene,
      coloursByChannel: prepared.coloursByChannel,
      textures: prepared.textures,
      resizeObserver
    };
    activeLiveDinoPreview = state;
    host.hidden = false;
    canvas.hidden = false;
    host.dataset.liveDinoModelState = "ready";
    renderer.render(scene, camera);
  } catch {
    if (sequence === liveDinoInitializationSequence && host.isConnected) {
      host.dataset.liveDinoModelState = "unavailable";
      host.hidden = true;
      canvas.hidden = true;
    }
  }
}

export function disposeLiveDinoModelPreview(): void {
  liveDinoInitializationSequence += 1;
  if (!activeLiveDinoPreview) return;
  activeLiveDinoPreview.resizeObserver.disconnect();
  disposeObject(activeLiveDinoPreview.root);
  for (const texture of activeLiveDinoPreview.textures) texture.dispose();
  activeLiveDinoPreview.renderer.dispose();
  activeLiveDinoPreview = undefined;
}

function applyLiveDinoColours(
  preview: ActiveLiveDinoPreview,
  colors: Array<{ field: string; r: number; g: number; b: number; a: number }> | null | undefined
): void {
  for (const color of colors ?? []) {
    for (const target of preview.coloursByChannel.get(color.field) ?? []) {
      target.setRGB(clampUnit(color.r), clampUnit(color.g), clampUnit(color.b));
    }
  }
}

export function updateSkinModelPreviewChannel(field: string, cssColor: string): void {
  for (const target of activePreview?.coloursByChannel.get(field) ?? []) target.set(cssColor);
}

export function updateSkinModelPreviewPattern(patternIndex: number): void {
  if (!activePreview) return;
  if (!Number.isSafeInteger(patternIndex) || patternIndex < 0 || patternIndex >= activePreview.patternTextures.adult.length) return;
  activePreview.selectedPattern = patternIndex;
  applyActivePreviewPattern(activePreview);
}

export function playSkinModelPreviewMotion(motion: SkinPreviewMotion): boolean {
  const preview = activePreview;
  const stage = skinPreviewLifeStage(preview?.growthPercent ?? 100);
  const next = preview?.motionActions.get(skinPreviewActionKey(stage, motion));
  if (!preview || !next) return false;
  const previous = preview.activeMotion && preview.activeLifeStage
    ? preview.motionActions.get(skinPreviewActionKey(preview.activeLifeStage, preview.activeMotion))
    : undefined;
  if (previous && previous !== next) previous.fadeOut(0.22);
  next.reset().fadeIn(0.22).play();
  preview.activeMotion = motion;
  preview.activeLifeStage = stage;
  const host = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  if (host) {
    host.querySelectorAll<HTMLButtonElement>("[data-skin-preview-motion]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.skinPreviewMotion === motion));
    });
  }
  return true;
}

export function updateSkinModelPreviewLighting(mode: SkinPreviewLighting, intensityPercent = 100): void {
  const preview = activePreview;
  if (!preview) return;
  const intensity = Number.isFinite(intensityPercent) ? Math.max(0.35, Math.min(1.5, intensityPercent / 100)) : 1;
  preview.lightingMode = mode;
  preview.lightIntensity = intensity;
  const values = mode === "daylight"
    ? { sky: 0xfff4d7, ground: 0x425243, hemisphere: 2.35, key: 3.2, rim: 0.7, keyColor: 0xfff0c8, rimColor: 0xb8d6bf }
    : mode === "night"
      ? { sky: 0x68809a, ground: 0x07120d, hemisphere: 0.85, key: 0.72, rim: 2.2, keyColor: 0x7897ad, rimColor: 0x58c99b }
      : { sky: 0xf1e7c7, ground: 0x16251c, hemisphere: 1.8, key: 2.4, rim: 1.2, keyColor: 0xfff2d0, rimColor: 0x7cbfa2 };
  preview.hemisphereLight.color.setHex(values.sky);
  preview.hemisphereLight.groundColor.setHex(values.ground);
  preview.hemisphereLight.intensity = values.hemisphere * intensity;
  preview.keyLight.color.setHex(values.keyColor);
  preview.keyLight.intensity = values.key * intensity;
  preview.rimLight.color.setHex(values.rimColor);
  preview.rimLight.intensity = values.rim * intensity;
}

function syncSkinPreviewMotionButtons(
  host: HTMLElement,
  actions: ReadonlyMap<SkinPreviewActionKey, AnimationAction>,
  stage: SkinPreviewLifeStage
): void {
  host.querySelectorAll<HTMLButtonElement>("[data-skin-preview-motion]").forEach((button) => {
    const motion = button.dataset.skinPreviewMotion as SkinPreviewMotion | undefined;
    const available = Boolean(motion && actions.has(skinPreviewActionKey(stage, motion)));
    button.disabled = !available;
    button.title = available
      ? `Preview ${button.textContent?.trim() ?? motion} motion`
      : `This approved model pack does not currently include ${button.textContent?.trim() ?? motion} motion`;
  });
  const label = host.querySelector<HTMLElement>("[data-skin-motion-label]");
  const copy = host.querySelector<HTMLElement>("[data-skin-motion-copy]");
  const stageCount = [...actions.keys()].filter((key) => key.startsWith(`${stage}:`)).length;
  if (label) label.textContent = stageCount ? `${stageCount} ${stage === "subadult" ? "sub-adult" : stage} actions ready` : "Static inspection";
  if (copy) copy.textContent = stageCount
    ? `Select an action captured from the game's ${stage === "subadult" ? "sub-adult" : stage} animation set. Unavailable clips remain locked.`
    : `This game build does not provide separate ${stage === "subadult" ? "sub-adult" : stage} actions for this species. Its exact growth shape, stage texture, camera, and lighting remain available.`;
}

export function skinPreviewGrowthMorphWeights(
  targetNames: readonly string[],
  growthPercent: number,
  visualGrowthCurve?: readonly GrowthCurveKey[]
): number[] {
  const growth = clampGrowth(growthPercent) / 100;
  const visualGrowth = evaluateGrowthCurve(visualGrowthCurve, growth);
  const normalized = targetNames.map((name) => name.toLowerCase().replace(/[^a-z0-9]/g, ""));
  const hatchling = normalized.findIndex((name) => name.includes("hatch"));
  const juvenile = normalized.findIndex((name) => name.includes("juvenile") || name.includes("juv"));
  const subadult = normalized.findIndex((name) => name.includes("subadult") || name.endsWith("sub"));
  const weights = targetNames.map(() => 0);

  if (hatchling >= 0) weights[hatchling] = descendingStageWeight(visualGrowth, 0, 0.25);
  if (juvenile >= 0) {
    weights[juvenile] = hatchling >= 0
      ? peakedStageWeight(visualGrowth, 0, 0.25, subadult >= 0 ? 0.5 : 1)
      : descendingStageWeight(visualGrowth, 0.25, subadult >= 0 ? 0.5 : 1);
  }
  if (subadult >= 0) weights[subadult] = peakedStageWeight(visualGrowth, 0.25, 0.5, 1);
  return weights;
}

export function evaluateGrowthCurve(keys: readonly GrowthCurveKey[] | undefined, growth: number): number {
  const input = clampUnit(growth);
  if (!keys?.length) return input;
  const ordered = [...keys]
    .filter((key) => Number.isFinite(key.time) && Number.isFinite(key.value))
    .sort((left, right) => left.time - right.time);
  if (!ordered.length) return input;
  if (input <= ordered[0]!.time) return clampUnit(ordered[0]!.value);
  for (let index = 0; index < ordered.length - 1; index += 1) {
    const left = ordered[index]!;
    const right = ordered[index + 1]!;
    if (input > right.time) continue;
    const duration = right.time - left.time;
    if (duration <= Number.EPSILON) return clampUnit(right.value);
    const alpha = clampUnit((input - left.time) / duration);
    if (left.interpolation !== "cubic") return clampUnit(left.value + (right.value - left.value) * alpha);
    const alpha2 = alpha * alpha;
    const alpha3 = alpha2 * alpha;
    const leave = (left.leaveTangent ?? 0) * duration;
    const arrive = (right.arriveTangent ?? 0) * duration;
    return clampUnit((2 * alpha3 - 3 * alpha2 + 1) * left.value
      + (alpha3 - 2 * alpha2 + alpha) * leave
      + (-2 * alpha3 + 3 * alpha2) * right.value
      + (alpha3 - alpha2) * arrive);
  }
  return clampUnit(ordered.at(-1)!.value);
}

function descendingStageWeight(value: number, holdUntil: number, zeroAt: number): number {
  if (value <= holdUntil) return 1;
  return clampUnit((zeroAt - value) / (zeroAt - holdUntil));
}

function peakedStageWeight(value: number, startsAt: number, peakAt: number, endsAt: number): number {
  if (value <= startsAt || value >= endsAt) return 0;
  if (value <= peakAt) return clampUnit((value - startsAt) / (peakAt - startsAt));
  return clampUnit((endsAt - value) / (endsAt - peakAt));
}

export function skinPreviewGrowthStage(growthPercent: number): string {
  const growth = Number.isFinite(growthPercent) ? Math.max(0, Math.min(100, growthPercent)) : 100;
  if (growth < 25) return "Hatchling";
  if (growth < 50) return "Juvenile";
  if (growth < 100) return "Sub-adult";
  return "Adult";
}

export function updateSkinModelPreviewGrowth(growthPercent: number): void {
  if (!activePreview) return;
  const previousStage = skinPreviewLifeStage(activePreview.growthPercent);
  activePreview.growthPercent = Number.isFinite(growthPercent) ? Math.max(0, Math.min(100, growthPercent)) : 100;
  for (const mesh of activePreview.growthMorphMeshes) {
    const names = Object.entries(mesh.morphTargetDictionary)
      .sort((left, right) => left[1] - right[1])
      .map(([name]) => name);
    const weights = skinPreviewGrowthMorphWeights(names, activePreview.growthPercent, activePreview.visualGrowthCurve);
    for (let index = 0; index < mesh.morphTargetInfluences.length; index += 1) {
      mesh.morphTargetInfluences[index] = weights[index] ?? 0;
    }
  }
  applyActivePreviewPattern(activePreview);
  applyGoreMorphs(activePreview.growthMorphMeshes, activePreview.goreIntensity);
  const nextStage = skinPreviewLifeStage(activePreview.growthPercent);
  if (previousStage !== nextStage || activePreview.activeLifeStage !== nextStage) {
    const requestedMotion = activePreview.activeMotion ?? "idle";
    activePreview.mixer?.stopAllAction();
    activePreview.activeMotion = undefined;
    activePreview.activeLifeStage = undefined;
    if (!playSkinModelPreviewMotion(requestedMotion)) playSkinModelPreviewMotion("idle");
  }
  const host = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  if (host) syncSkinPreviewMotionButtons(host, activePreview.motionActions, nextStage);
  activePreview.reframe();
}

export function updateSkinModelPreviewEffects(goreIntensity: number, settings: SkinPreviewEffectSettings): void {
  if (!activePreview) return;
  activePreview.goreIntensity = Number.isFinite(goreIntensity) ? Math.max(0, Math.min(100, goreIntensity)) : 0;
  activePreview.effectSettings = normalizePreviewEffectSettings(settings);
  applyGoreMorphs(activePreview.growthMorphMeshes, activePreview.goreIntensity);
  if (activePreview.effectSettings.effectMode === "none") applyCurrentEditorColours(activePreview.coloursByChannel);
}

export function updateSkinModelPreviewAnimation(animation: SkinAnimation | undefined): void {
  const host = document.querySelector<HTMLElement>("[data-skin-model-preview]");
  if (host) host.dataset.skinPreviewAnimation = animation?.enabled ? encodeURIComponent(JSON.stringify(animation)) : "";
  if (!activePreview) return;
  // Always restore the authored static palette first so a region that has just
  // been removed from the sequencer cannot retain its last animated shade.
  applyCurrentEditorColours(activePreview.coloursByChannel);
  activePreview.animation = animation?.enabled ? animation : undefined;
}

export function parseSkinPreviewAnimationState(encoded: string | undefined): SkinAnimation | undefined {
  if (!encoded) return undefined;
  try {
    const animation = JSON.parse(decodeURIComponent(encoded)) as SkinAnimation;
    return animation?.enabled === true && animation.schemaVersion === 1 && animation.tracks && typeof animation.tracks === "object"
      ? animation
      : undefined;
  } catch {
    return undefined;
  }
}

export function captureSkinPreviewCanvas(): HTMLCanvasElement | undefined {
  if (!activePreview) return undefined;
  activePreview.renderer.render(activePreview.scene, activePreview.camera);
  return activePreview.renderer.domElement;
}

export function updateMarketplaceSkinPreviewGrowth(host: HTMLElement, growthPercent: number): void {
  host.dataset.marketplaceSkinGrowth = String(clampGrowth(growthPercent));
  const preview = activeMarketplacePreviews.get(host);
  if (!preview) return;
  preview.growthPercent = clampGrowth(growthPercent);
  applyGrowthMorphs(preview.growthMorphMeshes, preview.growthPercent, preview.visualGrowthCurve);
  applyGrowthPattern(preview.bodyMaterials, preview.patternTextures, preview.selectedPattern, preview.growthPercent);
  preview.renderer.render(preview.scene, preview.camera);
}

export function initializeMarketplaceSkinPreviews(): void {
  for (const host of [...activeMarketplacePreviews.keys()]) {
    if (!host.isConnected) { marketplacePreviewObserver?.unobserve(host); disposeMarketplaceSkinPreview(host); }
  }
  const hosts = Array.from(document.querySelectorAll<HTMLElement>("[data-marketplace-skin-preview]"));
  if (!hosts.length) return;
  const sequence = marketplaceInitializationSequence;
  if (!marketplacePreviewObserver) {
    marketplacePreviewObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const host = entry.target as HTMLElement;
        if (entry.isIntersecting) {
          queueMarketplaceSkinPreview(host, sequence);
        } else {
          disposeMarketplaceSkinPreview(host);
        }
      }
    }, { rootMargin: "80px" });
  }
  for (const host of hosts) {
    if (host.dataset.previewObserved === "true") continue;
    host.dataset.previewObserved = "true";
    marketplacePreviewObserver.observe(host);
  }
}

export function disposeMarketplaceSkinPreviews(): void {
  marketplaceInitializationSequence += 1;
  marketplacePreviewObserver?.disconnect();
  marketplacePreviewObserver = undefined;
  loadingMarketplacePreviews.clear();
  for (const host of [...activeMarketplacePreviews.keys()]) disposeMarketplaceSkinPreview(host);
}

function queueMarketplaceSkinPreview(host: HTMLElement, sequence: number): void {
  if (activeMarketplacePreviews.has(host)
    || loadingMarketplacePreviews.has(host)
    || host.dataset.previewState === "unavailable") return;
  loadingMarketplacePreviews.add(host);
  host.dataset.previewState = "queued";
  marketplacePreviewLoadQueue = marketplacePreviewLoadQueue
    .catch(() => undefined)
    .then(async () => {
      if (sequence !== marketplaceInitializationSequence || !host.isConnected) return;
      await loadMarketplaceSkinPreview(host, sequence);
    })
    .finally(() => {
      loadingMarketplacePreviews.delete(host);
    });
}

async function loadMarketplaceSkinPreview(host: HTMLElement, sequence: number): Promise<void> {
  const canvas = host.querySelector<HTMLCanvasElement>("canvas");
  const species = host.dataset.marketplaceSkinSpecies?.trim();
  const encoded = host.dataset.marketplaceSkinPayload;
  if (!canvas || !species || !encoded || sequence !== marketplaceInitializationSequence) return;
  host.dataset.previewState = "loading";
  try {
    const payload = JSON.parse(decodeURIComponent(encoded)) as {
      patternIndex?: number;
      colors?: Array<{ field: string; r: number; g: number; b: number; a: number }>;
    };
    const manifest = await loadMarketplaceSkinManifest();
    const model = findApprovedSkinPreviewModel(manifest, species);
    if (!model || sequence !== marketplaceInitializationSequence || !host.isConnected) throw new Error("model unavailable");
    const [THREE, loaderModule] = await Promise.all([
      import("three"),
      import("three/addons/loaders/GLTFLoader.js")
    ]);
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "default" });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(30, 1, 0.01, 2_000);
    scene.add(new THREE.HemisphereLight(0xfff1cf, 0x14241a, 2.6));
    const keyLight = new THREE.DirectionalLight(0xffedc1, 3.5);
    keyLight.position.set(4, 7, 6);
    scene.add(keyLight);
    const gltf = await new loaderModule.GLTFLoader().loadAsync(skinAssetUrl(model.file));
    if (sequence !== marketplaceInitializationSequence || !host.isConnected) {
      disposeObject(gltf.scene);
      renderer.dispose();
      return;
    }
    const prepared = await prepareEvrimaMaterials(THREE, gltf.scene, model, true);
    const selectedPattern = Number.isSafeInteger(payload.patternIndex) && (payload.patternIndex ?? -1) >= 0
      ? payload.patternIndex ?? 0
      : 0;
    for (const color of payload.colors ?? []) {
      for (const target of prepared.coloursByChannel.get(color.field) ?? []) {
        target.setRGB(clampUnit(color.r), clampUnit(color.g), clampUnit(color.b));
      }
    }
    scene.add(gltf.scene);
    const bounds = new THREE.Box3().setFromObject(gltf.scene);
    const size = bounds.getSize(new THREE.Vector3());
    const centre = bounds.getCenter(new THREE.Vector3());
    gltf.scene.position.sub(centre);
    const diameter = Math.max(size.x, size.y, size.z, 0.1);
    const marketplaceFraming = host.classList.contains("marketplace-skin-model-detail") ? 0.58 : 0.62;
    const distance = diameter / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2))) * marketplaceFraming;
    camera.near = Math.max(distance / 1_000, 0.01);
    camera.far = distance * 100;
    camera.position.set(distance * 0.82, diameter * 0.1, distance * 0.92);
    camera.lookAt(0, -diameter * 0.04, 0);
    camera.updateProjectionMatrix();
    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    const growthPercent = clampGrowth(Number(host.dataset.marketplaceSkinGrowth ?? "100"));
    const state: ActiveMarketplacePreview = {
      host,
      renderer,
      scene,
      camera,
      root: gltf.scene,
      growthMorphMeshes: collectGrowthMorphMeshes(gltf.scene, model.rendering.eyeMaterial, model.rendering.growthMorphEyes),
      visualGrowthCurve: model.rendering.visualGrowthCurve,
      bodyMaterials: prepared.bodyMaterials,
      patternTextures: prepared.patternTextures,
      selectedPattern,
      growthPercent,
      textures: prepared.textures,
      resizeObserver
    };
    activeMarketplacePreviews.set(host, state);
    applyGrowthMorphs(state.growthMorphMeshes, state.growthPercent, state.visualGrowthCurve);
    applyGrowthPattern(state.bodyMaterials, state.patternTextures, state.selectedPattern, state.growthPercent);
    resize();
    host.dataset.previewState = "ready";
  } catch {
    if (sequence !== marketplaceInitializationSequence || !host.isConnected) return;
    host.dataset.previewState = "unavailable";
    const label = host.querySelector<HTMLElement>("span");
    if (label) label.textContent = "Exact 3D preview unavailable";
  }
}

function loadMarketplaceSkinManifest(): Promise<SkinPreviewManifest> {
  marketplaceManifestPromise ??= fetch("/portal/assets/skins/manifest.json", { cache: "no-store", credentials: "same-origin" })
    .then(async (response) => {
      if (!response.ok) throw new Error("model catalogue unavailable");
      return await response.json() as SkinPreviewManifest;
    })
    .catch((error: unknown) => {
      marketplaceManifestPromise = undefined;
      throw error;
    });
  return marketplaceManifestPromise;
}

function disposeMarketplaceSkinPreview(host: HTMLElement): void {
  const preview = activeMarketplacePreviews.get(host);
  if (!preview) return;
  preview.resizeObserver.disconnect();
  disposeObject(preview.root);
  for (const texture of preview.textures) texture.dispose();
  preview.renderer.dispose();
  activeMarketplacePreviews.delete(host);
}

export function skinPreviewPatternFiles(model: SkinPreviewModel): string[] {
  const files = model.rendering.patternFiles?.filter((value) => value.trim()) ?? [];
  return files.length ? files : [model.rendering.patternFile];
}

export function disposeSkinModelPreview(): void {
  initializationSequence += 1;
  if (!activePreview) return;
  window.cancelAnimationFrame(activePreview.frame);
  activePreview.mixer?.stopAllAction();
  activePreview.resizeObserver.disconnect();
  activePreview.removeControlListeners();
  activePreview.removeMixerListeners();
  activePreview.controls.dispose();
  disposeObject(activePreview.root);
  for (const texture of activePreview.textures) texture.dispose();
  activePreview.renderer.dispose();
  activePreview = undefined;
}

async function prepareEvrimaMaterials(
  THREE: typeof import("three"),
  root: Object3D,
  model: SkinPreviewModel,
  loadAllPatternVariants = false
): Promise<{
  coloursByChannel: Map<string, Color[]>;
  bodyMaterials: MeshStandardMaterial[];
  patternTextures: GrowthPatternTextures;
  textures: Texture[];
}> {
  if (model.rendering.mode !== "evrima-mask-v1") throw new Error("Unsupported skin preview material mode");
  const textureLoader = new THREE.TextureLoader();
  const loadTexture = (file: string): Promise<Texture> => textureLoader.loadAsync(skinAssetUrl(file)) as Promise<Texture>;
  const patternFiles = loadAllPatternVariants ? skinPreviewPatternFiles(model) : [model.rendering.patternFile];
  const [adultPatternTextures, juvenilePatternTexture, hatchlingPatternTexture, partTexture] = await Promise.all([
    Promise.all(patternFiles.map(loadTexture)),
    loadAllPatternVariants && model.rendering.juvenilePatternFile ? loadTexture(model.rendering.juvenilePatternFile) : undefined,
    loadAllPatternVariants && model.rendering.hatchlingPatternFile ? loadTexture(model.rendering.hatchlingPatternFile) : undefined,
    loadTexture(model.rendering.partMaskFile)
  ]);
  const stageTextures = [juvenilePatternTexture, hatchlingPatternTexture].filter((value): value is Texture => Boolean(value));
  for (const texture of [...adultPatternTextures, ...stageTextures, partTexture]) {
    texture.colorSpace = THREE.NoColorSpace;
    texture.flipY = false;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.needsUpdate = true;
  }

  const coloursByChannel = new Map<string, Color[]>();
  const colourFor = (field: string): Color => {
    const existing = coloursByChannel.get(field)?.[0];
    if (existing) return existing;
    const colour = new THREE.Color(0x808080);
    coloursByChannel.set(field, [colour]);
    return colour;
  };
  const paletteByLayer = new Map(Object.entries(model.rendering.paletteChannels)
    .map(([field, layer]) => [layer, colourFor(previewFieldForExtractedMask(field, model.rendering.fieldBindings))]));
  const partsByLayer = new Map(Object.entries(model.rendering.partChannels)
    .map(([field, layer]) => [layer, colourFor(previewFieldForExtractedMask(field, model.rendering.fieldBindings))]));
  const eyeColour = colourFor("EyesColor");
  const bodyMaterials: MeshStandardMaterial[] = [];
  let bodyFound = false;
  let eyesFound = false;

  root.traverse((object) => {
    const mesh = object as Mesh;
    if (!mesh.isMesh) return;
    const source = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const cloned = source.map((material) => {
      const clone = material.clone();
      if (!isColourMaterial(clone)) return clone;
      if (clone.name === model.rendering.bodyMaterial) {
        bodyFound = true;
        configureBodyMaterial(clone, adultPatternTextures[0]!, partTexture, paletteByLayer, partsByLayer);
        bodyMaterials.push(clone);
      } else if (clone.name === model.rendering.eyeMaterial) {
        eyesFound = true;
        clone.color = eyeColour;
        clone.vertexColors = false;
        clone.roughness = 0.34;
        clone.metalness = 0;
      }
      return clone;
    });
    mesh.material = Array.isArray(mesh.material) ? cloned : cloned[0]!;
  });
  if (!bodyFound || !eyesFound) {
    for (const texture of [...adultPatternTextures, ...stageTextures]) texture.dispose();
    partTexture.dispose();
    throw new Error(`Verified material names were not found (body=${bodyFound}, eyes=${eyesFound})`);
  }
  return {
    coloursByChannel,
    bodyMaterials,
    patternTextures: {
      adult: adultPatternTextures,
      juvenile: juvenilePatternTexture,
      hatchling: hatchlingPatternTexture
    },
    textures: [...adultPatternTextures, ...stageTextures, partTexture]
  };
}

function collectGrowthMorphMeshes(root: Object3D, eyeMaterial: string, growthMorphEyes = false): GrowthMorphMesh[] {
  const meshes: GrowthMorphMesh[] = [];
  root.traverse((object) => {
    const mesh = object as Mesh & Partial<GrowthMorphMesh>;
    if (!mesh.isMesh || !mesh.morphTargetDictionary || !mesh.morphTargetInfluences) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    // Rebuilt assets retain Unreal's source-vertex identity and can safely
    // morph the separately materialled eyes with the body. Older assets remain
    // fail-closed until their manifest explicitly records that rebuild.
    if (!growthMorphEyes && materials.some((material) => !skinPreviewMaterialSupportsGrowthMorph(material.name, eyeMaterial))) return;
    meshes.push({
      morphTargetDictionary: mesh.morphTargetDictionary,
      morphTargetInfluences: mesh.morphTargetInfluences
    });
  });
  return meshes;
}

function applyGrowthMorphs(meshes: GrowthMorphMesh[], growthPercent: number, visualGrowthCurve?: readonly GrowthCurveKey[]): void {
  for (const mesh of meshes) {
    const names = Object.entries(mesh.morphTargetDictionary)
      .sort((left, right) => left[1] - right[1])
      .map(([name]) => name);
    const weights = skinPreviewGrowthMorphWeights(names, growthPercent, visualGrowthCurve);
    for (let index = 0; index < mesh.morphTargetInfluences.length; index += 1) {
      mesh.morphTargetInfluences[index] = weights[index] ?? 0;
    }
  }
}

function applyGoreMorphs(meshes: GrowthMorphMesh[], goreIntensity: number): void {
  for (const mesh of meshes) {
    const names = Object.entries(mesh.morphTargetDictionary)
      .sort((left, right) => left[1] - right[1])
      .map(([name]) => name);
    const weights = skinPreviewGoreMorphWeights(names, goreIntensity);
    for (let index = 0; index < weights.length; index += 1) {
      if (weights[index] !== undefined) mesh.morphTargetInfluences[index] = weights[index]!;
    }
  }
}

export function skinPreviewGoreMorphWeights(targetNames: readonly string[], goreIntensity: number): Array<number | undefined> {
  const weight = clampUnit(goreIntensity / 100);
  return targetNames.map((name) => {
    const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, "");
    return normalized === "bodygore" || normalized === "leggore" || normalized === "neckgore" ? weight : undefined;
  });
}

function applySkinEffectPreview(preview: ActivePreview, timestampMs: number, host: HTMLElement): void {
  const settings = preview.effectSettings;
  if (settings.effectMode === "hdr_glitch") {
    applyPositiveHdrPreview(preview.coloursByChannel);
    return;
  }
  if (settings.effectMode === "rainbow") {
    applyRgbFade(preview.coloursByChannel, timestampMs, settings.effectSpeedSeconds);
    return;
  }
  const palette = previewEffectPalette(settings, host);
  const durationMs = settings.effectSpeedSeconds * 1000;
  const cycle = (timestampMs % durationMs) / durationMs;
  if (settings.effectMode === "glitch") {
    const burst = cycle > .82 && (Math.floor(cycle * 100) % 3 !== 0);
    const colour = hexRgb(burst ? (cycle > .92 ? palette[2]! : palette[1]!) : palette[0]!);
    applyUnifiedPreviewColour(preview.coloursByChannel, colour.r, colour.g, colour.b);
    return;
  }
  const progress = settings.effectPattern === "pulse" ? (1 - Math.cos(cycle * Math.PI * 2)) / 2 : cycle * palette.length;
  const index = settings.effectPattern === "pulse" ? 0 : Math.floor(progress) % palette.length;
  const nextIndex = settings.effectPattern === "pulse" ? 1 : (index + 1) % palette.length;
  const blend = settings.effectPattern === "pulse" ? progress : progress - Math.floor(progress);
  const left = hexRgb(palette[index] ?? palette[0]!);
  const right = hexRgb(palette[nextIndex] ?? palette[0]!);
  applyUnifiedPreviewColour(preview.coloursByChannel,
    left.r + (right.r - left.r) * blend,
    left.g + (right.g - left.g) * blend,
    left.b + (right.b - left.b) * blend);
}

function applyPositiveHdrPreview(coloursByChannel: Map<string, Color[]>): void {
  document.querySelectorAll<HTMLInputElement>("[data-skin-color]").forEach((input) => {
    const field = input.dataset.skinColor;
    if (!field || input.disabled) return;
    const base = hexRgb(input.value);
    const strongest = Math.max(base.r, base.g, base.b);
    const scale = strongest > 0 ? 2 / strongest : 1;
    for (const colour of coloursByChannel.get(field) ?? []) {
      colour.setRGB(Math.min(2, base.r * scale), Math.min(2, base.g * scale), Math.min(2, base.b * scale));
    }
  });
}

function applyRegionAnimationPreview(preview: ActivePreview, timestampMs: number): void {
  const animation = preview.animation;
  if (!animation?.enabled) return;
  const base = (timestampMs % (animation.cycleSeconds * 1_000)) / (animation.cycleSeconds * 1_000);
  for (const [field, track] of Object.entries(animation.tracks)) {
    if (!track || track.stops.length < 2) continue;
    let progress = (base + (track.phaseOffset ?? 0)) % 1;
    if (animation.pattern === "pulse") progress = (1 - Math.cos(progress * Math.PI * 2)) / 2;
    if (animation.pattern === "cascade") progress = (progress + Object.keys(animation.tracks).indexOf(field) * .073) % 1;
    const colour = samplePreviewAnimationTrack(track.stops, progress);
    for (const target of preview.coloursByChannel.get(field) ?? []) target.setRGB(colour.r, colour.g, colour.b);
  }
}

function samplePreviewAnimationTrack(stops: Array<{ position: number; color: string }>, progress: number): { r: number; g: number; b: number } {
  const ordered = [...stops].sort((left, right) => left.position - right.position);
  let left = ordered[0]!;
  let right = ordered.at(-1)!;
  let sample = progress;
  for (let index = 0; index < ordered.length - 1; index += 1) {
    if (progress >= ordered[index]!.position && progress <= ordered[index + 1]!.position) {
      left = ordered[index]!; right = ordered[index + 1]!; break;
    }
  }
  if (progress < ordered[0]!.position || progress > ordered.at(-1)!.position) {
    left = ordered.at(-1)!; right = ordered[0]!;
    if (sample < right.position) sample += 1;
  }
  const rightPosition = right.position <= left.position ? right.position + 1 : right.position;
  const amount = Math.max(0, Math.min(1, (sample - left.position) / Math.max(.0001, rightPosition - left.position)));
  const a = hexRgb(left.color);
  const b = hexRgb(right.color);
  return { r: a.r + (b.r - a.r) * amount, g: a.g + (b.g - a.g) * amount, b: a.b + (b.b - a.b) * amount };
}

function applyRgbFade(coloursByChannel: Map<string, Color[]>, timestampMs: number, speedSeconds: number): void {
  const hue = (timestampMs % (speedSeconds * 1000)) / (speedSeconds * 1000);
  const visited = new Set<Color>();
  for (const colours of coloursByChannel.values()) {
    for (const colour of colours) {
      if (visited.has(colour)) continue;
      visited.add(colour);
      colour.setHSL(hue, 1, 0.5);
    }
  }
}

function applyUnifiedPreviewColour(coloursByChannel: Map<string, Color[]>, r: number, g: number, b: number): void {
  const visited = new Set<Color>();
  for (const colours of coloursByChannel.values()) for (const colour of colours) {
    if (visited.has(colour)) continue;
    visited.add(colour);
    colour.setRGB(r, g, b);
  }
}

function previewEffectPalette(settings: SkinPreviewEffectSettings, host: HTMLElement): string[] {
  const curated: Partial<Record<SkinEffectMode, string[]>> = {
    bioluminescent: ["#061a24", "#16e0d1", "#5d7cff"],
    lava: ["#160503", "#ff3a0a", "#ffb000"],
    toxic: ["#160629", "#7cff00", "#dbff32"],
    frost: ["#172b46", "#75d8ff", "#f1fbff"],
    blood_moon: ["#090104", "#8b0015", "#ff274d"],
    aurora: ["#063f44", "#22e6a7", "#8e4dff"],
    glitch: ["#101018", "#ff176b", "#12e6ff"],
    ember: ["#120805", "#8f2d12", "#f2a23a"],
    deep_ocean: ["#020b1e", "#075a78", "#21d4c2"],
    phantom: ["#07070c", "#5a5575", "#d7d2ff"],
    solar_flare: ["#6b1000", "#ff6a00", "#fff2a0"],
    verdant: ["#07170d", "#247a36", "#b5e83d"]
  };
  if (settings.effectMode === "custom") return [settings.effectColor1, settings.effectColor2, settings.effectColor3];
  if (settings.effectMode === "environment") return environmentPalette(settings.environmentMode, host);
  return curated[settings.effectMode] ?? [settings.effectColor1, settings.effectColor2, settings.effectColor3];
}

function environmentPalette(mode: SkinEnvironmentMode, host: HTMLElement): string[] {
  const weather = host.dataset.skinPreviewWeather?.toLowerCase() ?? "clear";
  const time = Number(host.dataset.skinPreviewTime ?? "12");
  const latitude = Number(host.dataset.skinPreviewLatitude ?? "0");
  const longitude = Number(host.dataset.skinPreviewLongitude ?? "0");
  const dayNight = time >= 6 && time < 19 ? ["#245c3a", "#d2aa43", "#43bfa0"] : ["#07152e", "#315ac9", "#7b4bc4"];
  const weatherPalette = weather.includes("storm") ? ["#151827", "#655c8c", "#b8c8d8"]
    : weather.includes("rain") ? ["#082a37", "#147f9d", "#78d4df"]
      : weather.includes("fog") ? ["#293333", "#71817d", "#d0d6cf"]
        : weather.includes("wind") ? ["#19352b", "#55a882", "#c8d6a2"]
          : ["#2e5c35", "#d6b248", "#4cbf9a"];
  const edge = Math.abs(latitude) > 500_000 || Math.abs(longitude) > 500_000;
  const biome = edge ? ["#063b4a", "#147f89", "#8ac9ac"]
    : latitude > 180_000 ? ["#14392f", "#3f7862", "#b4d8cb"]
      : latitude < -180_000 ? ["#49311c", "#a76b29", "#6f9141"]
        : ["#173d26", "#4b8c45", "#b3a64a"];
  if (mode === "day_night") return dayNight;
  if (mode === "weather") return weatherPalette;
  if (mode === "biome") return biome;
  return weather.includes("clear") ? dayNight : weatherPalette;
}

function hexRgb(value: string): { r: number; g: number; b: number } {
  const safe = /^#[0-9a-f]{6}$/i.test(value) ? value : "#ffffff";
  const linear = (channel: string) => {
    const srgb = Number.parseInt(channel, 16) / 255;
    return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return { r: linear(safe.slice(1, 3)), g: linear(safe.slice(3, 5)), b: linear(safe.slice(5, 7)) };
}

function defaultSkinPreviewEffectSettings(): SkinPreviewEffectSettings {
  return { effectMode: "none", effectColor1: "#ff315c", effectColor2: "#20e3a2", effectColor3: "#4f7cff", effectSpeedSeconds: 30, effectPattern: "cycle", environmentMode: "adaptive" };
}

function readPreviewEffectSettingsFromDom(): SkinPreviewEffectSettings {
  return normalizePreviewEffectSettings({
    effectMode: (document.querySelector<HTMLSelectElement>("[data-skin-effect-mode]")?.value ?? "none") as SkinEffectMode,
    effectColor1: document.querySelector<HTMLInputElement>('[data-skin-effect-color="1"]')?.value ?? "#ff315c",
    effectColor2: document.querySelector<HTMLInputElement>('[data-skin-effect-color="2"]')?.value ?? "#20e3a2",
    effectColor3: document.querySelector<HTMLInputElement>('[data-skin-effect-color="3"]')?.value ?? "#4f7cff",
    effectSpeedSeconds: Number(document.querySelector<HTMLInputElement>("[data-skin-effect-speed]")?.value ?? "30"),
    effectPattern: (document.querySelector<HTMLSelectElement>("[data-skin-effect-pattern]")?.value ?? "cycle") as SkinEffectPattern,
    environmentMode: (document.querySelector<HTMLSelectElement>("[data-skin-environment-mode]")?.value ?? "adaptive") as SkinEnvironmentMode
  });
}

function normalizePreviewEffectSettings(settings: SkinPreviewEffectSettings): SkinPreviewEffectSettings {
  const modes: SkinEffectMode[] = ["none", "rainbow", "bioluminescent", "lava", "toxic", "frost", "blood_moon", "aurora", "glitch", "ember", "deep_ocean", "phantom", "solar_flare", "verdant", "hdr_glitch", "custom", "environment"];
  return {
    effectMode: modes.includes(settings.effectMode) ? settings.effectMode : "none",
    effectColor1: /^#[0-9a-f]{6}$/i.test(settings.effectColor1) ? settings.effectColor1 : "#ff315c",
    effectColor2: /^#[0-9a-f]{6}$/i.test(settings.effectColor2) ? settings.effectColor2 : "#20e3a2",
    effectColor3: /^#[0-9a-f]{6}$/i.test(settings.effectColor3) ? settings.effectColor3 : "#4f7cff",
    effectSpeedSeconds: Math.max(8, Math.min(120, Number.isFinite(settings.effectSpeedSeconds) ? settings.effectSpeedSeconds : 30)),
    effectPattern: settings.effectPattern === "pulse" ? "pulse" : "cycle",
    environmentMode: ["day_night", "weather", "biome", "adaptive"].includes(settings.environmentMode) ? settings.environmentMode : "adaptive"
  };
}

function applyActivePreviewPattern(preview: ActivePreview): void {
  applyGrowthPattern(preview.bodyMaterials, preview.patternTextures, preview.selectedPattern, preview.growthPercent);
}

function applyGrowthPattern(
  materials: MeshStandardMaterial[],
  textures: GrowthPatternTextures,
  selectedPattern: number,
  growthPercent: number
): void {
  const texture = growthPercent < 25
    ? textures.hatchling ?? textures.juvenile
    : growthPercent < 50
      ? textures.juvenile
      : undefined;
  if (texture) {
    for (const material of materials) {
      material.map = texture;
      material.needsUpdate = true;
    }
    return;
  }
  applyPreviewPattern(materials, textures.adult, selectedPattern);
}

function clampGrowth(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 100;
}

function applyPreviewPattern(materials: MeshStandardMaterial[], textures: Texture[], patternIndex: number): void {
  if (!Number.isSafeInteger(patternIndex) || patternIndex < 0 || patternIndex >= textures.length) return;
  for (const material of materials) {
    material.map = textures[patternIndex]!;
    material.needsUpdate = true;
  }
}

function configureBodyMaterial(
  material: MeshStandardMaterial,
  patternTexture: Texture,
  partTexture: Texture,
  palette: Map<string, Color>,
  parts: Map<string, Color>
): void {
  const required = (values: Map<string, Color>, key: string): Color => {
    const value = values.get(key);
    if (!value) throw new Error(`Skin preview is missing the ${key} colour mapping`);
    return value;
  };
  const colours = {
    red: required(palette, "red"),
    green: required(palette, "green"),
    blue: required(palette, "blue"),
    cyan: required(palette, "cyan"),
    magenta: required(palette, "magenta"),
    yellow: required(palette, "yellow"),
    partRed: required(parts, "red"),
    partGreen: required(parts, "green"),
    partBlue: required(parts, "blue")
  };
  material.color.set(0xffffff);
  material.vertexColors = false;
  material.map = patternTexture;
  material.roughness = 0.76;
  material.metalness = 0;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, {
      skinPartMap: { value: partTexture },
      skinRed: { value: colours.red },
      skinGreen: { value: colours.green },
      skinBlue: { value: colours.blue },
      skinCyan: { value: colours.cyan },
      skinMagenta: { value: colours.magenta },
      skinYellow: { value: colours.yellow },
      skinPartRed: { value: colours.partRed },
      skinPartGreen: { value: colours.partGreen },
      skinPartBlue: { value: colours.partBlue }
    });
    shader.fragmentShader = shader.fragmentShader
      .replace("void main() {", `
uniform sampler2D skinPartMap;
uniform vec3 skinRed;
uniform vec3 skinGreen;
uniform vec3 skinBlue;
uniform vec3 skinCyan;
uniform vec3 skinMagenta;
uniform vec3 skinYellow;
uniform vec3 skinPartRed;
uniform vec3 skinPartGreen;
uniform vec3 skinPartBlue;
void main() {`)
      .replace("#include <map_fragment>", `
vec3 skinMask = texture2D(map, vMapUv).rgb;
float weightRed = max(skinMask.r - max(skinMask.g, skinMask.b), 0.0);
float weightGreen = max(skinMask.g - max(skinMask.r, skinMask.b), 0.0);
float weightBlue = max(skinMask.b - max(skinMask.r, skinMask.g), 0.0);
float weightCyan = min(skinMask.g, skinMask.b) * (1.0 - skinMask.r);
float weightMagenta = min(skinMask.r, skinMask.b) * (1.0 - skinMask.g);
float weightYellow = min(skinMask.r, skinMask.g) * (1.0 - skinMask.b);
float skinWeight = max(weightRed + weightGreen + weightBlue + weightCyan + weightMagenta + weightYellow, 0.0001);
vec3 skinColour = (
  skinRed * weightRed + skinGreen * weightGreen + skinBlue * weightBlue +
  skinCyan * weightCyan + skinMagenta * weightMagenta + skinYellow * weightYellow
) / skinWeight;
if (skinWeight <= 0.00011) skinColour = skinBlue;
vec3 partMask = texture2D(skinPartMap, vMapUv).rgb;
vec3 partWeights = smoothstep(vec3(0.2), vec3(0.8), vec3(
  max(partMask.r - max(partMask.g, partMask.b), 0.0),
  max(partMask.g - max(partMask.r, partMask.b), 0.0),
  max(partMask.b - max(partMask.r, partMask.g), 0.0)
));
float partWeight = partWeights.r + partWeights.g + partWeights.b;
if (partWeight > 0.0001) {
  vec3 partColour = (
    skinPartRed * partWeights.r +
    skinPartGreen * partWeights.g +
    skinPartBlue * partWeights.b
  ) / partWeight;
  skinColour = mix(skinColour, partColour, clamp(partWeight, 0.0, 1.0));
}
diffuseColor.rgb *= skinColour;
`);
  };
  material.customProgramCacheKey = () => "sunny-isle-evrima-mask-v1";
  material.needsUpdate = true;
}

function isColourMaterial(material: Material): material is MeshStandardMaterial {
  return "color" in material && material.color != null;
}

function applyCurrentEditorColours(coloursByChannel: Map<string, Color[]>): void {
  document.querySelectorAll<HTMLInputElement>("[data-skin-color]").forEach((input) => {
    const field = input.dataset.skinColor;
    if (!field || input.disabled) return;
    for (const colour of coloursByChannel.get(field) ?? []) colour.set(input.value);
  });
}

function frameObject(
  THREE: typeof import("three"),
  object: Object3D,
  camera: PerspectiveCamera,
  controls: OrbitControls
): { cameraPosition: Vector3; target: Vector3 } {
  const bounds = new THREE.Box3().setFromObject(object, true);
  const size = bounds.getSize(new THREE.Vector3());
  const centre = bounds.getCenter(new THREE.Vector3());
  object.position.sub(centre);
  const diameter = Math.max(size.x, size.y, size.z, 0.1);
  const verticalFov = THREE.MathUtils.degToRad(camera.fov);
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(camera.aspect, 0.1));
  const fitFov = Math.min(verticalFov, horizontalFov);
  const distance = diameter / (2 * Math.tan(fitFov / 2)) * 1.18;
  camera.near = Math.max(distance / 1_000, 0.01);
  camera.far = distance * 100;
  camera.position.set(distance, diameter * 0.12, distance * 0.08);
  camera.updateProjectionMatrix();
  controls.target.set(0, 0, 0);
  controls.minDistance = distance * 0.45;
  controls.maxDistance = distance * 2.5;
  controls.update();
  return { cameraPosition: camera.position.clone(), target: controls.target.clone() };
}

function setPreviewState(
  host: HTMLElement,
  canvas: HTMLCanvasElement,
  unavailable: HTMLElement,
  resetButton: HTMLButtonElement,
  rotateButton: HTMLButtonElement,
  state: "checking" | "loading" | "ready" | "unavailable" | "error"
): void {
  host.dataset.skinPreviewState = state;
  host.setAttribute("aria-busy", String(state === "checking" || state === "loading"));
  const ready = state === "ready";
  canvas.hidden = !ready;
  unavailable.hidden = ready;
  resetButton.disabled = !ready;
  rotateButton.disabled = !ready;
  if (!ready) {
    rotateButton.setAttribute("aria-pressed", "false");
    rotateButton.classList.remove("active");
    const label = rotateButton.querySelector<HTMLElement>("[data-skin-rotate-label]");
    if (label) label.textContent = "Auto rotate";
  }
  const title = host.querySelector<HTMLElement>("[data-skin-preview-empty-title]");
  const copy = host.querySelector<HTMLElement>("[data-skin-preview-empty-copy]");
  if (!title || !copy) return;
  if (state === "checking") {
    title.textContent = "Checking model availability";
    copy.textContent = "Only licensed models verified against the current game build are displayed.";
  } else if (state === "loading") {
    title.textContent = "Loading verified model";
    copy.textContent = "Preparing the mesh, materials, and live colour mappings.";
  } else if (state === "unavailable") {
    title.textContent = "Verified model not available";
    copy.textContent = "You can still edit and apply the exact captured colour channels below.";
  } else if (state === "error") {
    title.textContent = "Preview could not be loaded";
    copy.textContent = "Colour editing remains available and does not depend on the 3D viewer.";
  }
}

function disposeObject(root: Object3D): void {
  root.traverse((object) => {
    const mesh = object as Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) material.dispose();
  });
}

function skinAssetUrl(file: string): string {
  return `/portal/assets/skins/${file.split("/").map(encodeURIComponent).join("/")}`;
}

function clampUnit(value: number): number {
  return Math.max(0, Math.min(1, value));
}
